import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../contexts/ProfileContext';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import toast from 'react-hot-toast';
import ProfilePictureModal from '../components/ProfilePictureModal';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const EYEBROW = { ...FREDOKA, color: "#0065F8", fontSize: 12, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase" };
const TITLE = { ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: "clamp(2rem, 5vw, 3.4rem)", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1.02, margin: "8px 0 6px" };
const SUBTITLE = { ...FREDOKA, color: "#5A6B85", fontSize: 16, margin: 0 };
const INPUT_BASE = {
  ...FREDOKA,
  width: "100%",
  background: "#FFFFFF",
  color: "#0B1A33",
  fontSize: 15,
  fontWeight: 500,
  border: "2px solid rgba(11,26,51,0.10)",
  borderRadius: 14,
  padding: "12px 16px",
  outline: "none",
  transition: "border-color 200ms, box-shadow 200ms",
};
const LABEL = { ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 };
const PRIMARY_PILL = {
  ...FREDOKA,
  background: "#0065F8",
  color: "#FFFFFF",
  fontWeight: 700,
  fontSize: 15,
  border: "none",
  borderRadius: 999,
  padding: "12px 22px",
  boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
  cursor: "pointer",
};
const SECONDARY_PILL = {
  ...FREDOKA,
  background: "#FFFFFF",
  color: "#5A6B85",
  fontWeight: 700,
  fontSize: 15,
  border: "2px solid rgba(11,26,51,0.10)",
  borderRadius: 999,
  padding: "11px 22px",
  boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
  cursor: "pointer",
};
const MODAL_CARD = {
  background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
  border: "2px solid rgba(11,26,51,0.06)",
  borderRadius: 28,
  boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
  fontFamily: "'Inter', 'Fredoka', sans-serif",
};
const Switch = ({ checked, onChange, accent = "#0065F8", accentShadow = "#073B8E" }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
    <div
      style={{
        width: 44,
        height: 24,
        background: checked ? accent : "rgba(11,26,51,0.18)",
        borderRadius: 999,
        boxShadow: checked ? `0px 2px 0px ${accentShadow}` : "inset 0px 1px 2px rgba(11,26,51,0.10)",
        transition: "background 200ms, box-shadow 200ms",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#FFFFFF",
          boxShadow: "0px 1px 3px rgba(11,26,51,0.20)",
          transition: "left 200ms",
        }}
      />
    </div>
  </label>
);

const ProfilesPage = () => {
  const navigate = useNavigate();
  const { profiles, currentProfile, switchProfile, refreshProfiles, requireParentalVerification } = useProfile();
  const { subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();

  const [isManaging, setIsManaging] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    starLimit: '',
    avatarUrl: '',
    isChild: false,
    allowStoryversePosting: false,
  });

  const hasUpgradedTier =
    subscriptionStatus?.subscriptionType &&
    ['FAMILY', 'UNLIMITED', 'NIKHIL'].includes(subscriptionStatus.subscriptionType);

  const canCreateMore = hasUpgradedTier && profiles.length < 5;

  const handleProfileClick = (profile) => {
    if (isManaging) {
      openEditModal(profile);
    } else {
      switchProfile(profile.id);
      toast.success(`Switched to ${profile.name}`);
      navigate('/storyverse');
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    try {
      const starLimit = formData.starLimit ? parseInt(formData.starLimit) : null;
      const response = await apiClient.createProfile(
        formData.name,
        formData.avatarUrl || null,
        starLimit,
        formData.isChild,
        formData.allowStoryversePosting
      );

      if (response?.status) {
        toast.success(`Profile "${formData.name}" created!`);
        setShowCreateModal(false);
        setFormData({ name: '', starLimit: '', avatarUrl: '', isChild: false, allowStoryversePosting: false });
        await refreshProfiles();
      } else {
        throw new Error(response?.error || 'Failed to create profile');
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error(error.message || 'Failed to create profile');
    }
  };

  const handleEdit = async () => {
    if (!formData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    try {
      const starLimit = formData.starLimit ? parseInt(formData.starLimit) : null;
      const response = await apiClient.updateProfile(selectedProfile.id, {
        name: formData.name,
        starLimit,
        avatarUrl: formData.avatarUrl || null,
        isChild: formData.isChild,
        allowStoryversePosting: formData.allowStoryversePosting,
      });

      if (response?.status) {
        toast.success(`Profile updated!`);
        setShowEditModal(false);
        setSelectedProfile(null);
        setFormData({ name: '', starLimit: '', avatarUrl: '', isChild: false, allowStoryversePosting: false });
        await refreshProfiles();
      } else {
        throw new Error(response?.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleAvatarUpdate = async (avatarUrl) => {
    if (!selectedProfile) return;

    try {
      const response = await apiClient.updateProfile(selectedProfile.id, {
        name: selectedProfile.name,
        starLimit: selectedProfile.star_limit,
        avatarUrl: avatarUrl,
      });

      if (response?.status) {
        toast.success(`Profile picture updated!`);
        await refreshProfiles();
      } else {
        throw new Error(response?.error || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error updating profile picture:', error);
      toast.error(error.message || 'Failed to update profile picture');
    }
  };

  const handleDelete = async () => {
    const executeDelete = async () => {
      try {
        const response = await apiClient.deleteProfile(selectedProfile.id);

        if (response?.status) {
          toast.success(`Profile deleted`);

          // If we deleted the current profile, switch to default
          if (currentProfile?.id === selectedProfile.id) {
            const defaultProfile = profiles.find((p) => p.is_default);
            if (defaultProfile) {
              switchProfile(defaultProfile.id);
            }
          }

          setShowDeleteModal(false);
          setSelectedProfile(null);
          await refreshProfiles();
        } else {
          throw new Error(response?.error || 'Failed to delete profile');
        }
      } catch (error) {
        console.error('Error deleting profile:', error);
        toast.error(error.message || 'Failed to delete profile');
      }
    };

    requireParentalVerification('delete this profile', executeDelete);
  };

  const openEditModal = (profile) => {
    setSelectedProfile(profile);
    setFormData({
      name: profile.name,
      starLimit: profile.star_limit !== null ? profile.star_limit.toString() : '',
      avatarUrl: profile.avatar_url || '',
      isChild: profile.is_child || false,
      allowStoryversePosting: profile.allow_storyverse_posting || false,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (profile) => {
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-16 relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <motion.div
        className="relative z-10 max-w-6xl w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span style={EYEBROW}>{isManaging ? "Manage Profiles" : "Family Profiles"}</span>
          <h1 style={TITLE}>
            {isManaging ? (
              <>Manage <span style={{ color: "#0065F8" }}>your family</span></>
            ) : (
              <>Who's <span style={{ color: "#0065F8" }}>watching?</span></>
            )}
          </h1>
          {!isManaging && <p style={SUBTITLE}>Pick a profile to keep your library separate.</p>}
        </motion.div>

        {/* Upgrade Banner */}
        {!hasUpgradedTier && !isManaging && (
          <motion.div
            className="max-w-3xl mx-auto mb-10 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
              borderRadius: 24,
              padding: "22px 24px",
              boxShadow: "0px 6px 0px rgba(7,59,142,0.40), 0px 18px 36px rgba(0,101,248,0.22)",
              color: "#FFFFFF",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <div style={{ position: "absolute", top: -50, right: -50, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.12)", filter: "blur(20px)" }} />
            <div className="flex items-center justify-between gap-4 relative z-10">
              <div>
                <span style={{ ...FREDOKA, background: "rgba(255,255,255,0.20)", color: "#FFFFFF", fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, border: "1.5px solid rgba(255,255,255,0.30)", display: "inline-block" }}>
                  Family Tier
                </span>
                <h3 style={{ ...FREDOKA, fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "8px 0 4px" }}>
                  Unlock multiple profiles
                </h3>
                <p style={{ ...FREDOKA, color: "rgba(255,255,255,0.88)", fontSize: 14, margin: 0 }}>
                  Up to 5 separate profiles with their own libraries on Family or Max.
                </p>
              </div>
              <button
                onClick={() => requireParentalVerification("upgrade subscription", () => navigate("/settings?tab=stats"))}
                className="transition active:scale-[0.97] whitespace-nowrap"
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#0065F8",
                  fontWeight: 800,
                  fontSize: 14,
                  border: "none",
                  borderRadius: 999,
                  padding: "12px 22px",
                  boxShadow: "0px 4px 0px rgba(7,59,142,0.30)",
                  cursor: "pointer",
                }}
              >
                Upgrade
              </button>
            </div>
          </motion.div>
        )}

        {/* Profiles Grid */}
        <div className="flex flex-wrap justify-center gap-8 mb-12">
          {profiles.map((profile, index) => {
            const isActive = currentProfile?.id === profile.id;
            return (
              <motion.div
                key={profile.id}
                className="flex flex-col items-center group cursor-pointer"
                onClick={() => handleProfileClick(profile)}
                initial={{ opacity: 0, scale: 0.8, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 + index * 0.08, ease: "easeOut" }}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.96 }}
              >
                {/* Avatar */}
                <div className="relative mb-4">
                  <div
                    className="overflow-hidden transition-all"
                    style={{
                      width: 168,
                      height: 168,
                      borderRadius: "50%",
                      background: "#FFFFFF",
                      border: isActive ? "5px solid #0065F8" : "5px solid #FFFFFF",
                      boxShadow: isActive
                        ? "0px 6px 0px #073B8E, 0px 0px 0px 4px rgba(0,101,248,0.18), 0px 18px 36px rgba(0,101,248,0.22)"
                        : "0px 5px 0px rgba(11,26,51,0.10), 0px 14px 28px rgba(11,26,51,0.10)",
                    }}
                  >
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={profile.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full flex items-center justify-center ${profile.avatar_url ? "hidden" : "flex"}`}
                      style={{ background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" }}
                    >
                      <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 64 }}>
                        {profile.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Edit Avatar Icon */}
                  {isManaging && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProfile(profile);
                        setShowProfilePictureModal(true);
                      }}
                      aria-label="Edit profile picture"
                      className="absolute transition active:scale-[0.94]"
                      style={{
                        bottom: 6,
                        left: 6,
                        width: 40,
                        height: 40,
                        background: "#0065F8",
                        color: "#FFFFFF",
                        border: "3px solid #FFFFFF",
                        borderRadius: "50%",
                        boxShadow: "0px 3px 0px #073B8E",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}

                  {isManaging && !profile.is_default && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(profile); }}
                      aria-label="Delete profile"
                      className="absolute transition active:scale-[0.94]"
                      style={{
                        top: 6,
                        right: 6,
                        width: 36,
                        height: 36,
                        background: "#DC2626",
                        color: "#FFFFFF",
                        border: "3px solid #FFFFFF",
                        borderRadius: "50%",
                        boxShadow: "0px 3px 0px #991B1B",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Name + chips */}
                <div className="text-center">
                  <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, margin: 0, transition: "color 200ms" }}>
                    {profile.name}
                  </h3>
                  <div className="flex justify-center gap-1.5 mt-2 flex-wrap">
                    {isActive && !isManaging && (
                      <span
                        className="inline-flex items-center gap-1"
                        style={{
                          ...FREDOKA,
                          background: "#0065F8",
                          color: "#FFFFFF",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          padding: "4px 10px",
                          borderRadius: 999,
                          boxShadow: "0px 2px 0px #073B8E",
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FFFFFF" }} />
                        Active
                      </span>
                    )}
                    {profile.is_child && (
                      <span
                        className="inline-flex items-center gap-1"
                        style={{
                          ...FREDOKA,
                          background: "linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)",
                          color: "#155E75",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: "1.5px solid rgba(6,182,212,0.30)",
                        }}
                      >
                        <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        Kid
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Add Profile Card */}
          {canCreateMore && isManaging && (
            <motion.div
              className="flex flex-col items-center group cursor-pointer"
              onClick={() => setShowCreateModal(true)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.15 + profiles.length * 0.08 }}
              whileHover={{ y: -6 }}
              whileTap={{ scale: 0.96 }}
            >
              <div
                className="flex items-center justify-center mb-4 transition-all"
                style={{
                  width: 168,
                  height: 168,
                  borderRadius: "50%",
                  background: "rgba(0,101,248,0.06)",
                  border: "3px dashed rgba(0,101,248,0.35)",
                }}
              >
                <svg width="56" height="56" fill="none" stroke="#0065F8" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 style={{ ...FREDOKA, color: "#0065F8", fontWeight: 700, fontSize: 20, margin: 0 }}>
                Add Profile
              </h3>
            </motion.div>
          )}
        </div>

        {/* Action Buttons */}
        <motion.div
          className="flex justify-center gap-3"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 + profiles.length * 0.08 }}
        >
          {isManaging ? (
            <button
              onClick={() => setIsManaging(false)}
              className="transition active:scale-[0.97]"
              style={PRIMARY_PILL}
            >
              Done
            </button>
          ) : (
            <button
              onClick={() => setIsManaging(true)}
              className="inline-flex items-center gap-2 transition active:scale-[0.97]"
              style={SECONDARY_PILL}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Manage Profiles
            </button>
          )}
        </motion.div>
      </motion.div>

      {/* Create / Edit shared form renderer */}
      {(showCreateModal || (showEditModal && selectedProfile)) && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4 overflow-y-auto"
          style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
          onClick={() => {
            if (showCreateModal) { setShowCreateModal(false); setFormData({ name: "", starLimit: "", avatarUrl: "", isChild: false, allowStoryversePosting: false }); }
            else { setShowEditModal(false); setSelectedProfile(null); setFormData({ name: "", starLimit: "", avatarUrl: "", isChild: false, allowStoryversePosting: false }); }
          }}
        >
          <div className="relative w-full max-w-md p-6 my-8" style={MODAL_CARD} onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <span style={EYEBROW}>{showCreateModal ? "New Profile" : "Edit Profile"}</span>
              <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 24, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "6px 0 0" }}>
                {showCreateModal ? "Create a profile" : "Update profile"}
              </h2>
            </div>

            <div className="space-y-4">
              {/* Avatar */}
              <div>
                <label style={LABEL}>Profile Picture</label>
                <div className="flex items-center gap-4">
                  <div
                    className="flex items-center justify-center overflow-hidden"
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                      border: "3px solid #FFFFFF",
                      boxShadow: "0px 3px 0px rgba(11,26,51,0.08), 0px 0px 0px 3px rgba(0,101,248,0.15)",
                      flexShrink: 0,
                    }}
                  >
                    {formData.avatarUrl ? (
                      <img src={formData.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 30 }}>
                        {formData.name ? formData.name.charAt(0).toUpperCase() : "?"}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowProfilePictureModal(true)}
                    className="transition active:scale-[0.97]"
                    style={{ ...SECONDARY_PILL, color: "#0065F8", borderColor: "rgba(0,101,248,0.25)", padding: "10px 16px", fontSize: 13 }}
                  >
                    {showCreateModal ? "Choose Picture" : "Change Picture"}
                  </button>
                </div>
              </div>

              {/* Name */}
              <div>
                <label style={LABEL}>Profile Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Emma, Jake, etc."
                  maxLength={50}
                  style={INPUT_BASE}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                />
              </div>

              {/* Star Limit */}
              <div>
                <label style={LABEL}>Star Limit (optional)</label>
                <input
                  type="number"
                  value={formData.starLimit}
                  onChange={(e) => setFormData({ ...formData, starLimit: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  min="0"
                  style={INPUT_BASE}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                />
                <p style={{ ...FREDOKA, color: "#94A3B8", fontSize: 12, marginTop: 6 }}>
                  {showEditModal && selectedProfile
                    ? `Current: ${selectedProfile.stars_spent || 0} stars used`
                    : "Set a spending limit for this profile"}
                </p>
              </div>

              {/* Child Profile Toggle */}
              <div
                className="flex items-center justify-between gap-3 p-3"
                style={{
                  background: "#FFFFFF",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 14,
                }}
              >
                <div>
                  <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 14 }}>Child Profile</div>
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, margin: "2px 0 0" }}>Enable parental controls and restrictions</p>
                </div>
                <Switch
                  checked={formData.isChild}
                  onChange={(e) => setFormData({ ...formData, isChild: e.target.checked, allowStoryversePosting: !e.target.checked })}
                />
              </div>

              {/* Child Settings */}
              {formData.isChild && (
                <div
                  className="space-y-3 p-3"
                  style={{
                    background: "linear-gradient(180deg, #E0F2FE 0%, #BAE6FD 100%)",
                    border: "2px solid rgba(2,132,199,0.25)",
                    borderRadius: 14,
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div style={{ ...FREDOKA, color: "#0C4A6E", fontWeight: 700, fontSize: 13 }}>Allow Storyverse Posting</div>
                      <p style={{ ...FREDOKA, color: "#075985", fontSize: 11, margin: "2px 0 0" }}>Let this profile share content publicly</p>
                    </div>
                    <Switch
                      checked={formData.allowStoryversePosting}
                      onChange={(e) => setFormData({ ...formData, allowStoryversePosting: e.target.checked })}
                      accent="#0369A1"
                      accentShadow="#0C4A6E"
                    />
                  </div>
                  <div
                    className="flex items-start gap-2 px-3 py-2"
                    style={{ background: "rgba(255,255,255,0.65)", borderRadius: 10, border: "1.5px solid rgba(2,132,199,0.22)" }}
                  >
                    <svg width="14" height="14" fill="none" stroke="#0369A1" strokeWidth="2.4" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 2 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p style={{ ...FREDOKA, color: "#0C4A6E", fontSize: 11, margin: 0, lineHeight: 1.4 }}>
                      Child profiles can't make purchases, delete content, or switch accounts without solving a math question.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  if (showCreateModal) { setShowCreateModal(false); setFormData({ name: "", starLimit: "", avatarUrl: "", isChild: false, allowStoryversePosting: false }); }
                  else { setShowEditModal(false); setSelectedProfile(null); setFormData({ name: "", starLimit: "", avatarUrl: "", isChild: false, allowStoryversePosting: false }); }
                }}
                className="flex-1 transition active:scale-[0.97]"
                style={{ ...SECONDARY_PILL, width: "100%" }}
              >
                Cancel
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleEdit}
                className="flex-1 transition active:scale-[0.97]"
                style={{ ...PRIMARY_PILL, width: "100%" }}
              >
                {showCreateModal ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProfile && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
          onClick={() => { setShowDeleteModal(false); setSelectedProfile(null); }}
        >
          <div
            className="relative w-full max-w-md p-6"
            style={{ ...MODAL_CARD, border: "2px solid rgba(220,38,38,0.22)", boxShadow: "0px 5px 0px rgba(220,38,38,0.10), 0px 24px 64px rgba(11,26,51,0.20)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 inline-flex items-center justify-center" style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(220,38,38,0.10)", color: "#DC2626" }}>
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <span style={EYEBROW}>Delete profile</span>
            <h2 style={{ ...FREDOKA, color: "#B91C1C", fontWeight: 700, fontSize: 24, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 8px" }}>
              Delete {selectedProfile.name}?
            </h2>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 20px" }}>
              This permanently deletes all characters, shows, stories, and storybooks tied to <strong style={{ color: "#0B1A33" }}>{selectedProfile.name}</strong>. It can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedProfile(null); }}
                className="flex-1 transition active:scale-[0.97]"
                style={{ ...SECONDARY_PILL, width: "100%" }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#DC2626",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 999,
                  padding: "12px 22px",
                  boxShadow: "0px 4px 0px #991B1B",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Picture Modal */}
      <ProfilePictureModal
        isOpen={showProfilePictureModal}
        onClose={() => {
          setShowProfilePictureModal(false);
          if (!showEditModal && !showCreateModal) {
            setSelectedProfile(null);
          }
        }}
        onSelect={async (avatarUrl) => {
          if (selectedProfile && !showEditModal && !showCreateModal) {
            // Direct avatar update from profile card edit icon
            await handleAvatarUpdate(avatarUrl);
            setShowProfilePictureModal(false);
            setSelectedProfile(null);
          } else {
            // Avatar selection for create or edit modal
            setFormData({ ...formData, avatarUrl });
            setShowProfilePictureModal(false);
          }
        }}
        currentAvatarUrl={selectedProfile?.avatar_url || formData.avatarUrl}
      />
    </div>
  );
};

export default ProfilesPage;
